import { useState, useEffect, useMemo } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import SEOHead from '@/components/feature/SEOHead';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ScrollToTop from '@/components/feature/ScrollToTop';
import { articles, categories, solutions } from '@/mocks/data';

const tabActiveClass: Record<string, string> = {
  yingyang: 'bg-primary-500 text-background-50',
  zaojiao: 'bg-accent-500 text-background-50',
  xiguan: 'bg-secondary-500 text-background-50',
};

const tabBadgeClass: Record<string, string> = {
  yingyang: 'bg-primary-100 text-primary-700',
  zaojiao: 'bg-accent-100 text-accent-700',
  xiguan: 'bg-secondary-100 text-secondary-700',
};

const tabDotClass: Record<string, string> = {
  yingyang: 'bg-primary-500',
  zaojiao: 'bg-accent-500',
  xiguan: 'bg-secondary-500',
};

export default function CategoryPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const activeTab = searchParams.get('tab') || 'yingyang';
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    setIsVisible(false);
    const timer = setTimeout(() => setIsVisible(true), 100);
    return () => clearTimeout(timer);
  }, [activeTab]);

  const activeCategory = useMemo(
    () => categories.find((c) => c.id === activeTab) || categories[0],
    [activeTab]
  );

  const activeSolution = useMemo(
    () => solutions.find((s) => s.id === activeTab),
    [activeTab]
  );

  const categoryArticles = useMemo(
    () => articles.filter((a) => a.category === activeTab),
    [activeTab]
  );

  const allArticles = useMemo(
    () => articles.filter((a) => a.category === activeTab),
    [activeTab]
  );

  const categorySchema = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: `悠宝三岁${activeCategory.name}`,
    description: `悠宝三岁${activeCategory.name}——专为3岁宝宝设计的科学${activeCategory.name}`,
    mainEntity: {
      '@type': 'ItemList',
      itemListElement: categoryArticles.map((a, i) => ({
        '@type': 'ListItem',
        position: i + 1,
        url: `https://ohpwbf.cn/article/${a.slug}`,
      })),
    },
  };

  return (
    <>
      <SEOHead
        title={activeCategory.name}
        description={`悠宝三岁${activeCategory.name}——${activeCategory.description}，为3岁宝宝提供专业科学的${activeCategory.name}。`}
        keywords={`悠宝三岁,${activeCategory.name},3岁宝宝,成长方案,育儿指导`}
        canonicalPath={`/category?tab=${activeTab}`}
        schema={categorySchema}
      />
      <ScrollToTop />
      <Navbar />
      <main className="pt-16">
        <div className="bg-background-100 border-b border-background-200/70">
          <div className="max-w-6xl mx-auto px-4 md:px-6 py-6">
            <Breadcrumb
              items={[
                { label: '首页', path: '/' },
                { label: '成长方案' },
              ]}
            />
          </div>
        </div>

        <section className="py-10 md:py-16 px-4 md:px-6 bg-background-100">
          <div className="max-w-6xl mx-auto text-center">
            <h1 className="font-heading text-3xl md:text-5xl text-foreground-950 mb-4">
              悠宝三岁成长方案
            </h1>
            <p className="text-foreground-600 text-sm md:text-base max-w-xl mx-auto mb-8">
              三大核心方案，全方位守护3岁宝宝的营养健康、智力发育和习惯养成
            </p>

            <div className="inline-flex bg-background-200/70 rounded-full p-1 gap-1">
              {categories.map((cat) => {
                const isActive = activeTab === cat.id;
                return (
                  <button
                    key={cat.id}
                    onClick={() => setSearchParams({ tab: cat.id })}
                    className={`flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-medium transition-all whitespace-nowrap cursor-pointer ${
                      isActive
                        ? tabActiveClass[cat.id]
                        : 'text-foreground-600 hover:text-foreground-900'
                    }`}
                  >
                    <i className={`${cat.icon} text-base`}></i>
                    {cat.name}
                  </button>
                );
              })}
            </div>
          </div>
        </section>

        {activeSolution && (
          <section className="py-10 md:py-14 px-4 md:px-6 bg-background-50">
            <div className="max-w-6xl mx-auto">
              <div className="flex flex-col lg:flex-row items-center gap-8 lg:gap-12">
                <div className="w-full lg:w-1/2">
                  <img
                    src={activeSolution.image}
                    alt={activeSolution.title}
                    className="w-full rounded-2xl object-cover aspect-[3/2]"
                  />
                </div>
                <div className="w-full lg:w-1/2">
                  <div className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full ${tabBadgeClass[activeTab]} text-xs font-medium mb-4`}>
                    <div className={`w-1.5 h-1.5 rounded-full ${tabDotClass[activeTab]}`}></div>
                    {activeSolution.title}
                  </div>
                  <h2 className="font-heading text-2xl md:text-3xl text-foreground-950 mb-4">
                    {activeSolution.subtitle}
                  </h2>
                  <p className="text-foreground-600 text-sm md:text-base leading-relaxed mb-6">
                    {activeSolution.description}
                  </p>
                  <div className="flex flex-wrap gap-2">
                    {activeSolution.features.map((f, i) => (
                      <span key={i} className={`px-3 py-1.5 rounded-full ${tabBadgeClass[activeTab]} text-sm`}>
                        <i className="ri-check-line mr-1"></i>
                        {f}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </section>
        )}

        <section className={`py-10 md:py-14 px-4 md:px-6 bg-background-100 transition-opacity duration-300 ${isVisible ? 'opacity-100' : 'opacity-0'}`}>
          <div className="max-w-6xl mx-auto">
            <h2 className="font-heading text-2xl md:text-3xl text-foreground-950 mb-8">
              {activeCategory.name}相关文章
            </h2>

            {allArticles.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {allArticles.map((article) => (
                  <Link
                    key={article.id}
                    to={`/article/${article.slug}`}
                    className="group bg-background-50 rounded-xl border border-background-200/70 overflow-hidden hover:border-background-300 transition-all cursor-pointer"
                  >
                    <div className="relative w-full h-44 overflow-hidden">
                      <img
                        src={article.coverImage}
                        alt={article.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                        loading="lazy"
                      />
                    </div>
                    <div className="p-5">
                      <h3 className="font-heading text-base text-foreground-900 mb-2 group-hover:text-primary-600 transition-colors line-clamp-2">
                        {article.title}
                      </h3>
                      <p className="text-foreground-600 text-sm leading-relaxed line-clamp-2 mb-3">
                        {article.excerpt}
                      </p>
                      <div className="flex items-center justify-between text-xs text-foreground-500">
                        <span>{article.date}</span>
                        <span className="flex items-center gap-1">
                          <i className="ri-time-line"></i>
                          {article.readTime}
                        </span>
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            ) : (
              <div className="text-center py-14">
                <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-background-200 flex items-center justify-center">
                  <i className="ri-file-list-3-line text-foreground-400 text-2xl"></i>
                </div>
                <p className="text-foreground-500 text-sm">该分类暂无文章，敬请期待</p>
              </div>
            )}
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}